<template>
  <div v-if="editor" class="rtt" role="toolbar" aria-label="Document formatting">
    <div class="rtt__group">
      <button
        v-for="(heading, idx) in headingButtons"
        :key="'h' + idx"
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.heading({ level: heading.level }) }"
        :title="heading.title"
        @click.prevent="run(() => commands.heading({ level: heading.level }))"
      >
        {{ heading.label }}
      </button>
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.paragraph() }"
        title="Paragraph"
        @click.prevent="run(commands.paragraph)"
      >
        P
      </button>
    </div>

    <span class="rtt__sep" aria-hidden="true"></span>

    <div class="rtt__group">
      <button
        type="button"
        class="rtt__btn rtt__btn--bold"
        :class="{ 'is-active': isActive.bold() }"
        title="Bold (Ctrl+B)"
        @click.prevent="run(commands.bold)"
      >B</button>
      <button
        type="button"
        class="rtt__btn rtt__btn--italic"
        :class="{ 'is-active': isActive.italic() }"
        title="Italic (Ctrl+I)"
        @click.prevent="run(commands.italic)"
      ><i>I</i></button>
      <button
        type="button"
        class="rtt__btn rtt__btn--underline"
        :class="{ 'is-active': isActive.underline() }"
        title="Underline (Ctrl+U)"
        @click.prevent="run(commands.underline)"
      ><u>U</u></button>
      <button
        type="button"
        class="rtt__btn rtt__btn--strike"
        :class="{ 'is-active': isActive.strike() }"
        title="Strikethrough"
        @click.prevent="run(commands.strike)"
      ><s>S</s></button>
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.code() }"
        title="Inline code"
        @click.prevent="run(commands.code)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
      </button>
    </div>

    <span class="rtt__sep" aria-hidden="true"></span>

    <div class="rtt__group">
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.bullet_list() }"
        title="Bullet list"
        @click.prevent="run(commands.bullet_list)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1.2"/><circle cx="4" cy="12" r="1.2"/><circle cx="4" cy="18" r="1.2"/></svg>
      </button>
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.ordered_list() }"
        title="Numbered list"
        @click.prevent="run(commands.ordered_list)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
      </button>
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.blockquote() }"
        title="Quote"
        @click.prevent="run(commands.blockquote)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h6v6H5a2 2 0 0 0-2 2v3"/><path d="M14 7h6v6h-4a2 2 0 0 0-2 2v3"/></svg>
      </button>
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.code_block() }"
        title="Code block"
        @click.prevent="run(commands.code_block)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><polyline points="9 9 7 12 9 15"/><polyline points="15 9 17 12 15 15"/></svg>
      </button>
    </div>

    <span class="rtt__sep" aria-hidden="true"></span>

    <div class="rtt__group">
      <button
        type="button"
        class="rtt__btn"
        :class="{ 'is-active': isActive.link() }"
        title="Insert link"
        @click.prevent="onLinkClick"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07l-1 1"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 1 0 7.07 7.07l1-1"/></svg>
      </button>
      <button
        type="button"
        class="rtt__btn"
        title="Horizontal rule"
        @click.prevent="run(commands.horizontal_rule)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="4" y1="12" x2="20" y2="12"/></svg>
      </button>
    </div>

    <span class="rtt__sep" aria-hidden="true"></span>

    <div class="rtt__group rtt__group--table">
      <div class="rtt-table">
        <button
          type="button"
          class="rtt__btn"
          :class="{ 'is-active': isInTable }"
          :title="isInTable ? 'Table options' : 'Insert table'"
          @click.prevent="toggleTableMenu"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <line x1="3" y1="16" x2="21" y2="16"/>
            <line x1="9" y1="4" x2="9" y2="20"/>
            <line x1="15" y1="4" x2="15" y2="20"/>
          </svg>
          <svg class="rtt-table__chev" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        <div v-if="showTableMenu" class="rtt-table__panel" @click.stop>
          <template v-if="!isInTable">
            <div class="rtt-table__head">Insert table</div>
            <div class="rtt-table__grid" @mouseleave="hoverRows = 0; hoverCols = 0">
              <div v-for="r in 6" :key="'tr' + r" class="rtt-table__row">
                <button
                  v-for="c in 8"
                  :key="'tc' + r + '-' + c"
                  type="button"
                  class="rtt-table__cell"
                  :class="{ 'is-active': r <= hoverRows && c <= hoverCols }"
                  @mouseover="hoverRows = r; hoverCols = c"
                  @focus="hoverRows = r; hoverCols = c"
                  @click.prevent="insertTable(r, c)"
                  :aria-label="`Insert ${r} by ${c} table`"
                />
              </div>
            </div>
            <div class="rtt-table__hint">{{ hoverRows && hoverCols ? `${hoverRows} × ${hoverCols} table` : 'Pick a size' }}</div>
            <label class="rtt-table__check">
              <input type="checkbox" v-model="newTableHeaderRow" />
              <span>Include header row</span>
            </label>
          </template>

          <template v-else>
            <div class="rtt-table__head">Table</div>
            <div class="rtt-table__list">
              <button type="button" class="rtt-table__item" @click.prevent="runTable('addRowBefore')">Row above</button>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('addRowAfter')">Row below</button>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('deleteRow')">Delete row</button>
              <div class="rtt-table__sep" role="separator"></div>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('addColumnBefore')">Column left</button>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('addColumnAfter')">Column right</button>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('deleteColumn')">Delete column</button>
              <div class="rtt-table__sep" role="separator"></div>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('toggleCellMerge')">Merge / split cell</button>
              <button type="button" class="rtt-table__item" @click.prevent="runTable('toggleHeaderRow')">Toggle header row</button>
              <div class="rtt-table__sep" role="separator"></div>
              <button type="button" class="rtt-table__item rtt-table__item--danger" @click.prevent="runTable('deleteTable')">Delete table</button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <span class="rtt__spacer" aria-hidden="true"></span>

    <div class="rtt__group">
      <button
        type="button"
        class="rtt__btn"
        :disabled="!canUndo"
        title="Undo (Ctrl+Z)"
        @click.prevent="run(commands.undo)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>
      </button>
      <button
        type="button"
        class="rtt__btn"
        :disabled="!canRedo"
        title="Redo (Ctrl+Shift+Z)"
        @click.prevent="run(commands.redo)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 15-6.7L21 13"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
const NOOP_ACTIVE = { heading: () => false, paragraph: () => false, bold: () => false, italic: () => false, underline: () => false, strike: () => false, code: () => false, bullet_list: () => false, ordered_list: () => false, blockquote: () => false, code_block: () => false, link: () => false, table: () => false }

export default {
  name: 'RichTextToolbar',
  props: {
    // The TipTap Editor instance — created by RichTextEditor and passed
    // down via the `editor-ready` event.
    editor: { type: Object, default: null }
  },
  data () {
    return {
      isActive: NOOP_ACTIVE,
      commands: {},
      canUndo: false,
      canRedo: false,
      isInTable: false,
      showTableMenu: false,
      hoverRows: 0,
      hoverCols: 0,
      newTableHeaderRow: true,
      headingButtons: [
        { level: 1, label: 'H1', title: 'Heading 1' },
        { level: 2, label: 'H2', title: 'Heading 2' },
        { level: 3, label: 'H3', title: 'Heading 3' }
      ],
      // Bound transaction handler stored so we can detach it on editor
      // swap. Kept as a plain property (not in data) so Vue treats it
      // as non-reactive instance state.
      transactionHandler: null
    }
  },
  watch: {
    editor: {
      immediate: true,
      handler (next, prev) {
        if (prev && this.transactionHandler) {
          prev.off('transaction', this.transactionHandler)
        }
        if (next) {
          this.transactionHandler = ({ state }) => this.refresh(state)
          next.on('transaction', this.transactionHandler)
          this.refresh(next.state)
        } else {
          this.isActive = NOOP_ACTIVE
          this.commands = {}
          this.canUndo = false
          this.canRedo = false
          this.isInTable = false
        }
      }
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.onDocumentMousedown)
    document.addEventListener('keydown', this.onDocumentKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.onDocumentMousedown)
    document.removeEventListener('keydown', this.onDocumentKeydown)
    if (this.editor && this.transactionHandler) {
      this.editor.off('transaction', this.transactionHandler)
    }
  },
  methods: {
    refresh (state) {
      if (!this.editor) return
      this.isActive = this.editor.isActive
      this.commands = this.editor.commands
      const history = state && state.history$
      this.canUndo = history ? history.done.eventCount > 0 : true
      this.canRedo = history ? history.undone.eventCount > 0 : true
      try { this.isInTable = Boolean(this.isActive.table && this.isActive.table()) }
      catch (_) { this.isInTable = false }
    },
    run (cmd) {
      if (typeof cmd === 'function') cmd()
      if (this.editor) this.editor.focus()
    },
    onLinkClick () {
      if (!this.editor) return
      const previous = this.isActive.link() ? this.editor.getMarkAttrs('link').href : ''
      // eslint-disable-next-line no-alert
      const url = window.prompt('Enter URL (leave empty to remove the link):', previous || 'https://')
      if (url === null) return
      if (url === '') {
        this.commands.link({ href: null })
      } else {
        this.commands.link({ href: url })
      }
      this.editor.focus()
    },
    toggleTableMenu () {
      this.showTableMenu = !this.showTableMenu
      if (this.showTableMenu) {
        this.hoverRows = 0
        this.hoverCols = 0
      }
    },
    insertTable (rows, cols) {
      if (!this.editor || !rows || !cols) return
      this.editor.commands.createTable({
        rowsCount: rows,
        colsCount: cols,
        withHeaderRow: Boolean(this.newTableHeaderRow)
      })
      this.editor.focus()
      this.showTableMenu = false
    },
    runTable (name) {
      if (!this.editor || !this.editor.commands[name]) return
      this.editor.commands[name]()
      this.editor.focus()
      this.showTableMenu = false
    },
    onDocumentMousedown (event) {
      if (!this.showTableMenu) return
      const panel = this.$el && this.$el.querySelector('.rtt-table')
      if (panel && !panel.contains(event.target)) {
        this.showTableMenu = false
      }
    },
    onDocumentKeydown (event) {
      if (event.key === 'Escape' && this.showTableMenu) {
        this.showTableMenu = false
      }
    }
  }
}
</script>

<style scoped>
.rtt {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  width: 100%;
}

.rtt__group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: #f8fafc;
  border: 1px solid #eef1f7;
  border-radius: 8px;
}

.rtt__sep {
  width: 1px;
  height: 22px;
  background: #e5e9f2;
  margin: 0 0.25rem;
}

.rtt__spacer { flex: 1 1 auto; }

.rtt__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  min-width: 32px;
  padding: 0 0.45rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
}

.rtt__btn i, .rtt__btn u, .rtt__btn s { font-style: normal; text-decoration: none; }
.rtt__btn--italic i { font-style: italic; }
.rtt__btn--underline u { text-decoration: underline; }
.rtt__btn--strike s { text-decoration: line-through; }
.rtt__btn--bold { font-weight: 800; }

.rtt__btn:hover:not(:disabled) {
  background: #ffffff;
  color: #0e5b46;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.rtt__btn.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.25);
}

.rtt__btn:disabled { opacity: 0.4; cursor: not-allowed; }
.rtt__btn:focus { outline: none; }
.rtt__btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.rtt__group--table { padding: 0; background: transparent; border: none; }

.rtt-table { position: relative; }
.rtt-table__chev { margin-left: 2px; opacity: 0.6; }

.rtt-table__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
  padding: 0.65rem 0.7rem 0.7rem;
  min-width: 240px;
}

.rtt-table__head {
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.45rem;
}

.rtt-table__grid { display: flex; flex-direction: column; gap: 2px; padding: 0.2rem 0.1rem; }
.rtt-table__row { display: flex; gap: 2px; }

.rtt-table__cell {
  width: 18px; height: 18px;
  border-radius: 3px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  padding: 0;
  cursor: pointer;
  transition: background 0.1s ease, border-color 0.1s ease;
}
.rtt-table__cell:hover { border-color: #16a07a; }
.rtt-table__cell.is-active { background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); border-color: transparent; }
.rtt-table__cell:focus { outline: none; }
.rtt-table__cell:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.4); }

.rtt-table__hint {
  margin: 0.4rem 0 0.55rem;
  font-size: 0.74rem;
  color: #475569;
  text-align: center;
  font-weight: 600;
}

.rtt-table__check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding-top: 0.45rem;
  border-top: 1px dashed #eef1f7;
  font-size: 0.78rem;
  color: #1f2a44;
  cursor: pointer;
  user-select: none;
}
.rtt-table__check input { accent-color: #16a07a; }

.rtt-table__list { display: flex; flex-direction: column; gap: 1px; min-width: 200px; }
.rtt-table__item {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.55rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
}
.rtt-table__item:hover { background: #f1f5f9; color: #0e5b46; }
.rtt-table__item--danger { color: #b91c1c; }
.rtt-table__item--danger:hover { background: #fef2f2; color: #991b1b; }
.rtt-table__sep { height: 1px; background: #eef1f7; margin: 0.25rem 0.15rem; }
</style>
